import { parseMultiValueParam, serializeMultiValueParam } from '@/utils/urlParams';

// Shared dot-notation prefix for metadata filter URL params
// (e.g. `metadata.call-reason=refill,transfer`). Matches the backend's HTTP
// dot-notation `metadata.key=value` and the QC page's QC_METADATA_PARAM_PREFIX.
const METADATA_PARAM_PREFIX = 'metadata.';

/**
 * Reads all `metadata.<key>` params from a URLSearchParams into a
 * { key: string[] } map. Values are comma-split (AND-of-ORs: several values on
 * one key is an OR / $in).
 */
export const readMetadataFilterParams = (
  searchParams: URLSearchParams,
): Record<string, string[]> => {
  const result: Record<string, string[]> = {};
  for (const [rawKey, value] of searchParams.entries()) {
    if (!rawKey.startsWith(METADATA_PARAM_PREFIX)) continue;
    const key = rawKey.slice(METADATA_PARAM_PREFIX.length);
    if (!key) continue;
    const values = parseMultiValueParam(value);
    if (values.length > 0) {
      result[key] = values;
    }
  }
  return result;
};

/**
 * Serializes a { key: string[] } metadata map to a { "metadata.<key>": "v1,v2" }
 * flat param map, suitable for spreading into updateSearchParams. Empty
 * selections map to `undefined` so the param is deleted.
 */
export const buildMetadataParamUpdates = (
  metadata: Record<string, string[]>,
): Record<string, string | undefined> => {
  const updates: Record<string, string | undefined> = {};
  for (const [key, values] of Object.entries(metadata)) {
    if (!key) continue;
    updates[`${METADATA_PARAM_PREFIX}${key}`] = serializeMultiValueParam(values);
  }
  return updates;
};

/**
 * Flattens a { key: string[] } metadata map to a flat { "metadata.<key>":
 * "v1,v2" } object for a request's query params (dot-notation the backend
 * reads). Skips empty selections.
 */
export const flattenMetadataForRequest = (
  metadata?: Record<string, string[]>,
): Record<string, string> => {
  const params: Record<string, string> = {};
  if (!metadata) return params;
  for (const [key, values] of Object.entries(metadata)) {
    if (!key || values.length === 0) continue;
    params[`${METADATA_PARAM_PREFIX}${key}`] = values.join(',');
  }
  return params;
};
